import React, { useState } from 'react';
import { Col, Form, Row, Table } from 'react-bootstrap';
import { FormattedDate, FormattedMessage, FormattedNumber, injectIntl } from 'react-intl';
import SorterList from '../../components/SorterList';
import FileUploader from '../../components/FileUploader';
import DateSelector from '../../components/DateSelector';
import { IMonthValue, formatDateMMYYYY, getPreviousMonth } from '../../util/DateUtils';
import ReportFileTransfer, { IReportFile } from '../../components/ReportFileTransfer';
import * as upload from '../../api/FileTransfer';
import { IHandlerReportSummary } from '../../util/DataModelInterface';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { sendHandlerReportData} from '../../api/HandlerData'
import { ConfirmModal, ReadyModal, WaitModal, AlertModal } from '../../components/ReportModals';
import TrafficLights from '../../components/TrafficLights';
import ReportTypeList from '../../components/ReportTypeList';

interface IImportHandlerReportProps {
  intl: any;
}

const ImportHandlerReport = (props: IImportHandlerReportProps) => {
  const [,setState] = useState({}); // just to force re-render when upload status changes
  const [date, setDate] = useState(getPreviousMonth());
  const [files, setFiles] = useState([] as IReportFile[]);
  const [modalWaitOpen, setWaitModalOpen] = useState(false);
  const [modalConfOpen, setConfirmationModalOpen] = useState(false);
  const [modalReadyOpen, setReadyModalOpen] = useState(false);
  const [modalAlertOpen, setAlertModalOpen] = useState(false);
  const [modalText, setModalText] = useState('');
  const [alertText, setAlertText] = useState('');
  const [alertInfo, setAlertInfo] = useState('');
  const [sorter, setSorter] = useState('');
  const [sorterName, setSorterName] = useState('');
  const [summary, setSummary] = useState({checksum: 0, crt: 0, sda: 0, lda: 0, cold: 0, ce: 0, solar: 0, scrap: 0, total: 0} as IHandlerReportSummary);
  const [hideSummary, setHideSummary] = useState(true);
  const [showError, setShowError] = useState(false);
  const [sendDisabled, setSendDisabled] = useState(true);

  const deleteFile = (file: IReportFile) => {
    // Deletes file by name, because we don't have any other ids
    if (file.status === 'ready') {
      resetSummary();
    }
    setFiles(files => files.filter((f) => f.name !== file.name));
    checkIfSendIsDisabled();
  }

  const resetSummary = () => {
    setHideSummary(true);
    setSummary( {checksum: 0, crt: 0, sda: 0, lda: 0, cold: 0, ce: 0, solar: 0, scrap: 0, total: 0} as IHandlerReportSummary );
  }

  const clear = (clearMonth: boolean = false) => {
    if (clearMonth) {
      setDate(getPreviousMonth());
    }
    setFiles([] as IReportFile[]);
    setWaitModalOpen(false);
    setConfirmationModalOpen(false);
    setAlertModalOpen(false);
    setModalText('');
    setAlertText('');
    setAlertInfo('');
    setSorter("");
    setSorterName('');
    resetSummary();
  }

  const onFileSelect = (files: File[]) => {
    for(let i = 0; i < files.length; i++) addFile(files[i]);
    checkIfSendIsDisabled();
  }

  const addFile = (file: File) => {
    const newFile: IReportFile = {
      name: file.name,
      file,
      status: 'added',
      delete: () => {
        deleteFile(newFile);
      },
      progress: 0,
      type: '',
      intl: props.intl
    };
    // don't add the same file twice
    if (!files.find((f) => f.name === file.name)) {
      files.push(newFile);
      setFiles(files);
      resetSummary();
    }
  }

  const allFilesReady = () => files.every((f) => ['ready', 'finished'].includes(f.status));

  const sendFile = (file: IReportFile) => {
    file.status = 'inprogress';
    checkIfSendIsDisabled();
    upload.sendSortReport(
      file,
      sorter,
      file.type,
      formatDateMMYYYY(date),
      (progress) => { // onProgress
        file.progress = progress;
        setState({});
      },
      (response: any) => { // onReady
        if (response.errors && Array.isArray(response.errors) && response.errors.length > 0) {
          file.status = 'error';
          file.errorMessage = props.intl.formatMessage({id: 'filetransfer.error.rowParsingError'});
        } else {
          file.status = 'ready';
        }
        file.response = response;
        if(allFilesReady()) calculateSummary();
        setHideSummary(!allFilesReady());
        checkIfSendIsDisabled();
        setState({});
      },
      (errorMsg: string, data?: any) => {
        file.status = 'error';
        file.response = data;
        try {
          file.errorMessage = props.intl.formatMessage({id: errorMsg});
        } catch (e) {
          console.error('No translation found for', errorMsg);
          file.errorMessage = errorMsg;
        }
        setState({});
      }
    );
  }

  const calculateSummary = () => {
    let summary_sum = {checksum: 0, crt: 0, sda: 0, lda: 0, cold: 0, ce: 0, solar: 0, scrap: 0, total: 0} as IHandlerReportSummary;
    files.forEach((file) => {
      console.log(file.response);
      if(file.response && file.response.summary && file.status !== 'error') {
        summary_sum.crt += parseFloat(file.response.summary.CRT);
        summary_sum.ce += parseFloat(file.response.summary.CE);
        summary_sum.cold += parseFloat(file.response.summary.COLD);
        summary_sum.lda += parseFloat(file.response.summary.LDA);
        summary_sum.sda += parseFloat(file.response.summary.SDA);
        summary_sum.solar += parseFloat(file.response.summary.SOLAR);
        summary_sum.scrap += parseFloat(file.response.summary.SCRAP);
        summary_sum.total += parseFloat(file.response.summary.TOTAL);
      }
    });
    summary_sum.checksum = Math.round(((summary_sum.crt+summary_sum.sda+summary_sum.lda+summary_sum.cold+summary_sum.ce+summary_sum.solar+summary_sum.scrap)+ Number.EPSILON) * 100) / 100;
    setSummary(summary_sum);
  }

  const sendFiles = () => {
    resetSummary();
    files.forEach((f) => {
      if (!['finished', 'error'].includes(f.status)) {
        sendFile(f);
      }
    });
  }

  const getFileList = () => {
    const list: JSX.Element[] = [];
    let index: number = 0;
    files.forEach((file) => {
      list.push (
        <div className="row" key={++index}>
          <ReportFileTransfer {...file}/>
          <ReportTypeList sorter={sorter} multiselect={false} setType={(e) => changeReportType(file, e)} selected={file.type} />
        </div>
      );
    })
    return list;
  }

  const changeReportType = (file: IReportFile, type: string) => {
    if (type !== '') { // do not reset the value, there is never need to do this
      file.type = type;
      const index = files.findIndex((f) => f.name === file.name);
      files[index].type = type;
      files[index].progress = 0;
      if (type !== '') {
        files[index].status = 'ready';
      } else {
        files[index].status = 'added';
      }
      setFiles(files);
    }
    checkIfSendIsDisabled();
  }

  // Some creative state handling is needed here to catch the changes made to the state immediately before.
  // Idea from: https://medium.com/ableneo/react-setstate-does-not-immediately-update-the-state-84dbd26f67d5
  const checkIfSendIsDisabled = () => {
    setFiles((fs: IReportFile[]) => {
      let result = Boolean(fs.length === 0);
      fs.forEach((f) => {
        if (f.type === '' || ['inprogress', 'error'].includes(f.status)) {
          result = true;
        }
      });
      setSorter((s: string) => {
        if (!s || s === '') {
          result = true;
        }
        setSendDisabled(result);
        return s;
      });
      return fs;
    })
  }

  const getImportResultDataTable = () => {
    return (
    <div className="generalReportImportResult" hidden={hideSummary}>
      <div className="d-flex flex-md-row flex-column-reverse">
        <div className="reportResultHeader">
          <h3 className="monthRange">
            <FormattedDate  value={new Date(date.year, date.month)}
                            month="long" year="numeric" />
          </h3>
          <h3 className="reportHeader">
            <FormattedMessage id="handlerreports.header"/>
          </h3>
        </div>
        <div className="handlerNameHeader ml-md-auto">
          <h1>{sorterName}</h1>
        </div>
      </div>

      <div className="handlerReportSummary">
        <Table className="table table-sm">
          <tbody>
            <tr className="checksum">
              <th scope="row"><FormattedMessage id="handlerreports.import.resulttable.checksum"/></th>
              <td className="weightSum"><FormattedNumber value={summary.checksum} maximumFractionDigits={2} minimumFractionDigits={2}/></td>
              <td><FormattedMessage id="handlerreports.import.resulttable.kg"/></td>
              <td onClick={() => setShowError(!showError)}><FontAwesomeIcon icon={summary.checksum !== summary.total ? "exclamation-circle" : "check-circle"}
                                   color={summary.checksum !== summary.total ? "var(--serty-orange)" : "var(--serty-green)"}/></td>
            { showError && summary.total !== summary.checksum &&
              <div className="error-modal">
                <div className="modal-close-button" onClick={() => setShowError(false)}>X</div>
                <FormattedMessage id="handlerreports.import.resulttable.summismatch"/>
              </div>
            }
            </tr>
            <tr className="table-heading">
              <th colSpan={4} scope="row" className="sumHeader"><FormattedMessage id="handlerreports.import.resulttable.classedser"/></th>
            </tr>
            <tr>
              <th scope="row"><FormattedMessage id="handlerreports.import.resulttable.crt"/></th>
              <td className="weight"><div ><FormattedNumber value={summary.crt} maximumFractionDigits={2} minimumFractionDigits={2}/></div></td>
              <td><FormattedMessage id="handlerreports.import.resulttable.kg"/></td>
              <td></td>
            </tr>
            <tr>
              <th scope="row"><FormattedMessage id="handlerreports.import.resulttable.sda"/></th>
              <td className="weight"><div><FormattedNumber value={summary.sda} maximumFractionDigits={2} minimumFractionDigits={2}/></div></td>
              <td><FormattedMessage id="handlerreports.import.resulttable.kg"/></td>
              <td></td>
            </tr>
            <tr>
              <th scope="row"><FormattedMessage id="handlerreports.import.resulttable.lda"/></th>
              <td className="weight"><div><FormattedNumber value={summary.lda} maximumFractionDigits={2} minimumFractionDigits={2}/></div></td>
              <td><FormattedMessage id="handlerreports.import.resulttable.kg"/></td>
              <td></td>
            </tr>
            <tr>
              <th scope="row"><FormattedMessage id="handlerreports.import.resulttable.cold"/></th>
              <td className="weight"><div><FormattedNumber value={summary.cold} maximumFractionDigits={2} minimumFractionDigits={2}/></div></td>
              <td><FormattedMessage id="handlerreports.import.resulttable.kg"/></td>
              <td></td>
            </tr>
            <tr>
              <th scope="row"><FormattedMessage id="handlerreports.import.resulttable.ce"/></th>
              <td className="weight"><div><FormattedNumber value={summary.ce} maximumFractionDigits={2} minimumFractionDigits={2}/></div></td>
              <td><FormattedMessage id="handlerreports.import.resulttable.kg"/></td>
              <td></td>
            </tr>
            <tr>
            <th scope="row"><FormattedMessage id="handlerreports.import.resulttable.solar"/></th>
              <td className="weight"><div><FormattedNumber value={summary.solar} maximumFractionDigits={2} minimumFractionDigits={2}/></div></td>
              <td><FormattedMessage id="handlerreports.import.resulttable.kg"/></td>
              <td></td>
            </tr>            
            <tr>
              <th scope="row"><FormattedMessage id="handlerreports.import.resulttable.scrap"/></th>
              <td className="weight"><div><FormattedNumber value={summary.scrap} maximumFractionDigits={2} minimumFractionDigits={2}/></div></td>
              <td><FormattedMessage id="handlerreports.import.resulttable.kg"/></td>
              <td></td>
            </tr>
            <tr className="table-footer checksum">
              <th scope="row" className="sumHeader"><FormattedMessage id="handlerreports.import.resulttable.sum"/></th>
              <td className="weightSum"><FormattedNumber value={summary.total} maximumFractionDigits={2} minimumFractionDigits={2}/></td>
              <td><FormattedMessage id="handlerreports.import.resulttable.kg"/></td>
              <td onClick={() => setShowError(!showError)}><FontAwesomeIcon icon={summary.total !== summary.checksum ? "exclamation-circle" : "check-circle"}
                                   color={summary.total !== summary.checksum ? "var(--serty-orange)" : "var(--serty-green)"}/></td>
            </tr>
          </tbody>
        </Table>
      </div>
      <div className="action-buttons">
        <button className="clear-button btn btn-secondary"
                onClick={() => {
                  setModalText('');
                  setConfirmationModalOpen(true);
                }}>
          <FormattedMessage id="handlerreports.import.reject"/>
        </button>
        <button className="send-button btn btn-primary"
                onClick={() => {
                  setWaitModalOpen(true);
                  sendHandlerReportData(sorter, formatDateMMYYYY(date), composeJSON()).then((response) => {
                    if(response && !response.error) {
                      clear(false);
                      setReadyModalOpen(true);
                      setTimeout(() => setReadyModalOpen(false), 2000);
                    } else {
                      setAlertText('handlerreports.import.error');
                      setAlertInfo(response.error);
                      setAlertModalOpen(true);
                    }
                  }).finally(() => setWaitModalOpen(false));
                }}>
          <FormattedMessage id="handlerreports.import.accept"/>
        </button>
      </div>
    </div>);
  }

  const composeJSON = () => {
    let json: any[] = [];
    files.forEach((file) => {
        json = json.concat(file.response.rows);
    });
    return json;
  }

  const clearFileStatuses = () => {
    files.forEach((f) => {
      if (f.status === 'error') {
        f.status = 'ready';
        f.progress = 0;
      }
    });
  }


  return (
    <div>
      <WaitModal
        messageId="handlerreports.import.saving"
        modalOpen={modalWaitOpen}
        setModalOpen={(open: boolean) => setWaitModalOpen(open)}
      />
      <ReadyModal
        messageId="handlerreports.import.ready"
        modalOpen={modalReadyOpen}
        setModalOpen={(open: boolean) => setReadyModalOpen(open)}
      />
      <ConfirmModal
        modalOpen={modalConfOpen}
        labelId={'handlerreports.import.rejectreport'}
        messageId={"handlerreports.import.rejectreporttext"}
        modalText={modalText}
        submitId={'handlerreports.import.edit'} dismissId={'handlerreports.import.reject'}
        onDismiss={() => { setConfirmationModalOpen(false); clear(); } }
        onSubmit={() => { setConfirmationModalOpen(false); } } />
      <AlertModal
        modalOpen={modalAlertOpen}
        messageId={alertText}
        modalText={alertInfo}
        onDismiss={() => { setAlertModalOpen(false); clear(); }} />
      <h1>
        <FormattedMessage id="handlerreports.header"/> - <FormattedMessage id="handlerreports.import.header"/>
      </h1>
      <div className="search-wrap">
        <Row className="d-flex flex-row">
          <Col md="6">
            <Form.Group>
              <label htmlFor="sorter-select"><FormattedMessage id="handlerreports.import.sorter"/></label>
              <select
                className="sorter-select form-control"
                id="sorter-select"
                value={sorter}
                onChange={(e) => {
                  setSorter(e.target.value);
                  setSorterName(e.target.options[e.target.selectedIndex].text);
                  clearFileStatuses();
                  checkIfSendIsDisabled();
                }}>
                { SorterList(props.intl) }
              </select>
            </Form.Group>
          </Col>
          <Col md="6">
            <Form.Group>
            <label><FormattedMessage id="handlerreports.import.reportmonth"/></label>
            <DateSelector startPeriod={date} 
                          onChange={(value: IMonthValue) => { setDate(value); clearFileStatuses(); checkIfSendIsDisabled(); } } 
                          dateFormat={'yyyy-MM'} />
            </Form.Group>
          </Col>
          { sorter &&
          <Col md="12">
            <Form.Group>
              <TrafficLights type = {sorter} report = {'sorter'} period = {date}/>
            </Form.Group>
          </Col> }
          <Col md="12">
            <Form.Group>
              <label htmlFor="file-upload"><FormattedMessage id="handlerreports.import.importreport"/></label>
              <FileUploader id="file-upload" onFileSelect={onFileSelect} label={props.intl.formatMessage({id: 'handlerreports.import.uploadfile'})}/>
              { getFileList() }
            </Form.Group>
          </Col>
          <Col className="form-buttons d-flex flex-row">
            <button
              className="send-button btn btn-primary ml-auto"
              disabled={sendDisabled}
              onClick={() => {
                sendFiles();
              }}
            >
              <FormattedMessage id="handlerreports.import.send"/>
            </button>
          </Col>
        </Row>
      </div>
      <div className="search-results">
        { getImportResultDataTable() }
      </div>
    </div>
  );
}

export default injectIntl(ImportHandlerReport);
